import { microcopySetToResourceBundles } from '@lib/coveo/microcopySetToResourceBundles';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';

/** Category slug to Phosphor icon name mapping */
export const CATEGORY_ICON_NAMES: Record<string, string> = {
  '3d': 'Cube',
  '2d': 'ImageSquare',
  audio: 'Headphones',
  tools: 'Wrench',
  vfx: 'MagicWand',
  templates: 'CopySimple',
  ai: 'Circuitry',
  'add-ons': 'Plug',
  essentials: 'PuzzlePiece',
  industry: 'GearSix',
  sdk: 'Circuitry',
  decentralization: 'ShareNetwork',
  animation: 'Play',
};

export function getCategoryIconName(categoryKey: string): string | undefined {
  return CATEGORY_ICON_NAMES[categoryKey.toLowerCase()];
}

/** Resolves display name from microcopy, falling back to formatted slug. */
export function getCategoryDisplayName(
  categoryKey: string,
  searchValuesMicrocopy?: MicrocopyItem[]
): string {
  if (searchValuesMicrocopy) {
    const categoryResourceBundle = microcopySetToResourceBundles(
      searchValuesMicrocopy
    ).ec_category;
    const microcopyValue = categoryResourceBundle?.[categoryKey.toLowerCase()];
    if (microcopyValue) {
      return microcopyValue;
    }
  }

  return formatCategorySlug(categoryKey);
}

/**
 * Extracts the parent (top-level) category from various formats.
 * Handles Coveo arrays (['3D', 'Characters']) and API strings ('3D/Characters').
 * Preserves original case -- callers handle normalization.
 */
export function getParentCategory(
  category: string | string[] | undefined | null
): string | undefined {
  if (!category) {
    return undefined;
  }

  if (Array.isArray(category)) {
    return category[0] || undefined;
  }

  const firstSegment = category.split(/[/>]/)[0]?.trim();
  return firstSegment || undefined;
}

/**
 * Formats a category slug into a display name.
 * Short slugs (1-3 chars) become uppercase ("vfx" -> "VFX"),
 * others become title case ("add-ons" -> "Add-Ons").
 */
export function formatCategorySlug(slug: string): string {
  const lowerSlug = slug.toLowerCase();

  if (lowerSlug.length <= 3 && !lowerSlug.includes('-')) {
    return lowerSlug.toUpperCase();
  }

  return lowerSlug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('-');
}
